import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { HelpCircle, Trash2 } from 'lucide-react';
import {
  CCAConfigQuestionEnriched,
  CCAQuestion,
} from '../../../../../../../utils/types/cca_types';
import { getSubdomainColor, toTitleCase } from '../utils/ccaHelpers';

interface Props {
  assignedQuestions: CCAConfigQuestionEnriched[];
  questionMap: Record<string, CCAQuestion>;
  isLoading: boolean;
  isGenerating?: boolean;
  onRemove?: (questionId: string) => void;
  onClearAll?: () => void;
}

export const AssignedQuestionsList: React.FC<Props> = ({
  assignedQuestions,
  questionMap,
  isLoading,
  isGenerating,
  onRemove,
  onClearAll,
}) => {
  if (isGenerating) {
    return (
      <Paper
        variant='outlined'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 3,
          borderStyle: 'dashed',
          borderColor: 'divider',
          py: 8,
          color: 'text.secondary',
        }}
      >
        <CircularProgress size={24} color='primary' sx={{ mb: 1.5 }} />
        <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Generating questions…
        </Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant='rounded' height={56} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );
  }

  if (assignedQuestions.length === 0) {
    return (
      <Paper
        variant='outlined'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 3,
          borderStyle: 'dashed',
          borderColor: 'divider',
          py: 8,
          color: 'text.secondary',
        }}
      >
        <HelpCircle size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
        <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
          No questions assigned yet
        </Typography>
        <Typography variant='caption' sx={{ mt: 0.5, color: 'text.secondary' }}>
          Use Quick Build or Manual Selection to add questions
        </Typography>
      </Paper>
    );
  }

  const saCount = assignedQuestions.filter(
    (q) => q.questionType === 'short_answer',
  ).length;
  const mcCount = assignedQuestions.filter(
    (q) => q.questionType === 'multiple_choice',
  ).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>
          {assignedQuestions.length} Question
          {assignedQuestions.length !== 1 ? 's' : ''} Assigned
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${saCount} SA`}
            size='small'
            color='secondary'
            variant='outlined'
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
          <Chip
            label={`${mcCount} MC`}
            size='small'
            color='primary'
            variant='outlined'
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
          {onClearAll && (
            <Button
              onClick={onClearAll}
              size='small'
              color='error'
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
            >
              Clear all
            </Button>
          )}
        </Box>
      </Box>

      {/* List */}
      <Stack spacing={1.5}>
        {assignedQuestions.map((cq, i) => {
          const fullQ = questionMap[cq.questionId];
          return (
            <Paper
              key={cq.id || `${cq.questionId}-${i}`}
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                p: 1.5,
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: (theme) => theme.shadows[1],
                },
              }}
            >
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  mt: 0.25,
                }}
              >
                {i + 1}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {fullQ ? (
                  <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {fullQ.question.text}
                  </Typography>
                ) : (
                  <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    Loading…
                  </Typography>
                )}

                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip
                    label={toTitleCase(cq.subdomain)}
                    size='small'
                    className={getSubdomainColor(cq.subdomain)}
                    sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                  />
                  <Chip
                    label={cq.questionType === 'multiple_choice' ? 'MC' : 'SA'}
                    size='small'
                    color='info'
                    variant='outlined'
                    sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                  />
                  {fullQ && (
                    <Chip
                      label={fullQ.question.imageKey ? 'image' : 'wordy'}
                      size='small'
                      sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                    />
                  )}
                  <Chip
                    label={`Lv ${cq.level}`}
                    size='small'
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                </Box>

                {fullQ?.idealAnswer && (
                  <Typography variant='caption' sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    <Typography component='span' variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Ideal answer:
                    </Typography>{' '}
                    {fullQ.idealAnswer}
                  </Typography>
                )}
              </Box>

              {onRemove && (
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onRemove(cq.questionId)}
                  aria-label='Remove question'
                >
                  <Trash2 size={16} />
                </IconButton>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AssignedQuestionsList;
